import { useCallback, useMemo, useState } from 'react';

export type UseDisclosureStepHandlerConfig = {
  isDisclosureVisible: boolean;
  brandContent: boolean;
  brandOrganic: boolean;
  onVisibilityChange: (isDisclosureVisible: boolean) => void;
  onBrandContentChange: (brandContent: boolean) => void;
  onBrandOrganicChange: (brandOrganic: boolean) => void;
  onBackClick: () => void;
};

export type UseDisclosureStepHandlerResult = {
  isUntouched: boolean;
  localBrandContent: boolean | undefined;
  localBrandOrganic: boolean | undefined;
  onSetLocalBrandContent: (brandContent: boolean) => void;
  onSetLocalBrandOrganic: (brandOrganic: boolean) => void;
  onToggleDisclosureVisibility: (isVisible: boolean) => void;
  onBackClick: () => void;
  onSaveChanges: () => void;
};

export default function useDisclosureStepHandler({
  isDisclosureVisible,
  brandContent,
  brandOrganic,
  onVisibilityChange,
  onBrandContentChange,
  onBrandOrganicChange,
  onBackClick,
}: UseDisclosureStepHandlerConfig) {
  const [localBrandContent, setLocalBrandContent] = useState<
    boolean | undefined
  >();
  const [localBrandOrganic, setLocalBrandOrganic] = useState<
    boolean | undefined
  >();

  const isUntouched = useMemo(
    (): boolean =>
      localBrandContent === undefined && localBrandOrganic === undefined,
    [localBrandContent, localBrandOrganic],
  );

  const handleToggleDisclosureVisibility = useCallback(
    (isVisible: boolean): void => {
      onVisibilityChange(isVisible);
      setLocalBrandContent(isVisible ? brandContent : false);
      setLocalBrandOrganic(isVisible ? brandOrganic : false);
    },
    [brandContent, brandOrganic, onVisibilityChange],
  );

  const handleResetValues = useCallback((): void => {
    setLocalBrandContent(undefined);
    setLocalBrandOrganic(undefined);
  }, []);

  const handleBackClick = useCallback((): void => {
    if (!brandContent && !brandOrganic) {
      onVisibilityChange(false);
    }

    if (!isUntouched && !isDisclosureVisible) {
      onVisibilityChange(true);
    }

    setLocalBrandContent(brandContent);
    setLocalBrandOrganic(brandOrganic);
    onBackClick();
    handleResetValues();
  }, [
    brandContent,
    brandOrganic,
    handleResetValues,
    isDisclosureVisible,
    isUntouched,
    onBackClick,
    onVisibilityChange,
  ]);

  const handleSaveChanges = useCallback((): void => {
    if (localBrandContent === false && localBrandOrganic === false) {
      onVisibilityChange(false);
    }

    onBrandContentChange(localBrandContent ?? brandContent);
    onBrandOrganicChange(localBrandOrganic ?? brandOrganic);
    onBackClick();
    handleResetValues();
  }, [
    brandContent,
    brandOrganic,
    handleResetValues,
    localBrandContent,
    localBrandOrganic,
    onBackClick,
    onBrandContentChange,
    onBrandOrganicChange,
    onVisibilityChange,
  ]);

  return {
    isUntouched,
    localBrandContent,
    localBrandOrganic,
    onSetLocalBrandContent: setLocalBrandContent,
    onSetLocalBrandOrganic: setLocalBrandOrganic,
    onToggleDisclosureVisibility: handleToggleDisclosureVisibility,
    onBackClick: handleBackClick,
    onSaveChanges: handleSaveChanges,
  };
}
